import { hslToHex } from "./hslToHex";

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parse a `#RGB` or `#RRGGBB` hex string into RGB channels (0–255). */
export function parseHexColor(value: string): Rgb | null {
  const hex = value.trim().replace(/^#/, "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  if (full.length !== 6 || /[^0-9a-f]/i.test(full)) {
    return null;
  }
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16)
  };
}

/** WCAG relative luminance of an RGB color, 0 (black) – 1 (white). */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const s = value / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Convert RGB (0–255) to HSL (h: 0–360, s/l: 0–1). */
export function rgbToHsl({ r, g, b }: Rgb): {
  h: number;
  s: number;
  l: number;
} {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) {
    return { h: 0, s: 0, l };
  }
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case rn:
      h = (gn - bn) / d + (gn < bn ? 6 : 0);
      break;
    case gn:
      h = (bn - rn) / d + 2;
      break;
    default:
      h = (rn - gn) / d + 4;
  }
  return { h: h * 60, s, l };
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export interface IconColorOptions {
  /** Ink lightness on light backgrounds (0–1). Default 0.34. */
  darkLightness?: number;
  /** Ink lightness on dark backgrounds (0–1). Default 0.92. */
  lightLightness?: number;
  /** Minimum saturation applied to colorful backgrounds (0–1). Default 0.5. */
  minSaturation?: number;
  /** Below this background saturation the ink becomes neutral gray. Default 0.12. */
  grayThreshold?: number;
  /** Returned when the background can't be parsed. Default "#404040". */
  fallback?: string;
}

/**
 * Derive a readable icon ("ink") color that coordinates with a background color:
 * the same hue, but more saturated and darker on light backgrounds — and lighter on
 * dark ones so it stays legible. Mirrors the pastel-tile / saturated-icon pairing in
 * the designs (e.g. a pale green tile yields a deeper green icon).
 */
export function getIconColorForBackground(
  background: string,
  options: IconColorOptions = {}
): string {
  const {
    darkLightness = 0.34,
    lightLightness = 0.92,
    minSaturation = 0.5,
    grayThreshold = 0.12,
    fallback = "#404040"
  } = options;

  const rgb = parseHexColor(background);
  if (!rgb) {
    return fallback;
  }

  const { h, s } = rgbToHsl(rgb);
  const isLightBackground = relativeLuminance(rgb) > 0.4;

  // Near-gray backgrounds get a neutral ink; colorful ones keep their hue, boosted.
  const inkSaturation =
    s < grayThreshold ? 0 : clamp(Math.max(s, minSaturation), 0, 0.85);
  const inkLightness = isLightBackground ? darkLightness : lightLightness;

  return hslToHex(h, inkSaturation, inkLightness);
}
